import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { User, Mail, Phone, Shield, Clock, Key, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UserProfile() {
  const { user } = useAuth();
  const [editingInfo, setEditingInfo] = useState(false);
  const [myInfo, setMyInfo] = useState({});

  // Security: password + PIN self-service
  const [newPass, setNewPass] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [savingPass, setSavingPass] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [savingPin, setSavingPin] = useState(false);

  const changeMyPassword = async () => {
    if (newPass.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    setSavingPass(true);
    try {
      await axios.post(`${API}/api/auth/set-my-password`, { new_password: newPass });
      toast.success('Password updated');
      setNewPass(''); setConfirmPass('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update password');
    } finally { setSavingPass(false); }
  };

  const changeMyPin = async () => {
    if (!/^\d{4}$/.test(newPin)) { toast.error('PIN must be exactly 4 digits'); return; }
    if (newPin !== confirmPin) { toast.error('PINs do not match'); return; }
    setSavingPin(true);
    try {
      await axios.post(`${API}/api/auth/set-my-pin`, { pin: newPin });
      toast.success('Kiosk PIN updated');
      setNewPin(''); setConfirmPin('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update PIN');
    } finally { setSavingPin(false); }
  };

  useEffect(() => {
    if (user) setMyInfo({
      phone_number: user.phone_number || '',
      email: user.email || '',
      address: user.address || '',
      emergency_contact: user.emergency_contact || '',
      emergency_phone: user.emergency_phone || '',
    });
  }, [user]);

  const saveMyInfo = async () => {
    try {
      await axios.put(`${API}/api/users/${user.id}`, myInfo);
      toast.success('Information updated');
      setEditingInfo(false);
    } catch { toast.error('Failed to save'); }
  };

  if (!user) return null;

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>

      {/* Avatar + name */}
      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white text-2xl font-bold">
            {user.full_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">{user.full_name}</h2>
            <p className="text-sm text-slate-600">{(user.role || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</p>
            {user.shop_number && <p className="text-xs text-slate-500">Employee #{user.shop_number}</p>}
          </div>
        </div>

        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-slate-700">Contact Information</h3>
          {!editingInfo && <button onClick={() => setEditingInfo(true)} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Edit</button>}
        </div>

        {editingInfo ? (
          <div className="space-y-3">
            <div>
              <label className="text-xs text-slate-600 font-medium">Phone Number *</label>
              <input type="tel" value={myInfo.phone_number} onChange={e => setMyInfo({...myInfo, phone_number: e.target.value})}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="+1 (548) 328-1711" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Email *</label>
              <input type="email" value={myInfo.email} onChange={e => setMyInfo({...myInfo, email: e.target.value})}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Home Address</label>
              <input type="text" value={myInfo.address} onChange={e => setMyInfo({...myInfo, address: e.target.value})}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="123 Main St, City, ON" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Emergency Contact Name</label>
              <input type="text" value={myInfo.emergency_contact} onChange={e => setMyInfo({...myInfo, emergency_contact: e.target.value})}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="Jane Smith" />
            </div>
            <div>
              <label className="text-xs text-slate-600 font-medium">Emergency Contact Phone</label>
              <input type="tel" value={myInfo.emergency_phone} onChange={e => setMyInfo({...myInfo, emergency_phone: e.target.value})}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none" placeholder="+1 (555) 123-4567" />
            </div>
            <div className="flex gap-2 pt-2">
              <button onClick={saveMyInfo} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Save</button>
              <button onClick={() => setEditingInfo(false)} className="px-5 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm font-medium">Cancel</button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {[
              { icon: Phone, label: 'Phone', value: user.phone_number || '—' },
              { icon: Mail, label: 'Email', value: user.email },
              { icon: User, label: 'Address', value: user.address || '—' },
              { icon: Shield, label: 'Emergency Contact', value: user.emergency_contact ? `${user.emergency_contact} — ${user.emergency_phone || ''}` : '—' },
            ].map(f => (
              <div key={f.label} className="flex items-center gap-3 py-2 border-b border-slate-200 last:border-0">
                <f.icon className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-500">{f.label}</p>
                  <p className="text-sm text-slate-900 font-medium">{f.value}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security: Password & Kiosk PIN */}
      <div className="bg-white/70 backdrop-blur-sm rounded-xl border border-slate-200 shadow-sm p-6" data-testid="security-card">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Shield className="w-4 h-4 text-slate-500" /> Security
        </h3>

        {/* Change Password */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-slate-500" />
            <p className="text-sm font-medium text-slate-800">Change Login Password</p>
          </div>
          <div className="space-y-3">
            <input
              type="password" value={newPass} onChange={e => setNewPass(e.target.value)}
              placeholder="New password" autoComplete="new-password" data-testid="profile-new-password"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500"
            />
            <input
              type="password" value={confirmPass} onChange={e => setConfirmPass(e.target.value)}
              placeholder="Confirm new password" autoComplete="new-password" data-testid="profile-confirm-password"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm outline-none focus:border-blue-500"
            />
            <button
              onClick={changeMyPassword} disabled={savingPass || !newPass} data-testid="profile-save-password-btn"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
            >
              {savingPass ? 'Saving…' : 'Update Password'}
            </button>
          </div>
        </div>

        {/* Change Kiosk PIN */}
        <div className="pt-5 border-t border-slate-200">
          <div className="flex items-center gap-2 mb-1">
            <Key className="w-4 h-4 text-slate-500" />
            <p className="text-sm font-medium text-slate-800">Change Kiosk PIN</p>
          </div>
          <p className="text-xs text-slate-500 mb-3">Your 4-digit PIN for clocking in at the shop kiosk.</p>
          <div className="space-y-3">
            <input
              type="password" inputMode="numeric" maxLength={4} value={newPin}
              onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
              placeholder="New 4-digit PIN" autoComplete="off" data-testid="profile-new-pin"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm tracking-widest outline-none focus:border-blue-500"
            />
            <input
              type="password" inputMode="numeric" maxLength={4} value={confirmPin}
              onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
              placeholder="Confirm PIN" autoComplete="off" data-testid="profile-confirm-pin"
              className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-slate-900 text-sm tracking-widest outline-none focus:border-blue-500"
            />
            <button
              onClick={changeMyPin} disabled={savingPin || !newPin} data-testid="profile-save-pin-btn"
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium"
            >
              {savingPin ? 'Saving…' : 'Update PIN'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
